import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'

export interface Workspace {
  id: string
  name: string
  role: 'admin' | 'user' | 'readonly'
  rbac_settings?: any
}

export interface WorkspaceInvite {
  id: string
  workspace_id: string
  email: string
  role: 'admin' | 'user' | 'readonly'
  status: 'pending' | 'accepted' | 'declined'
  created_at: string
  workspace_name?: string
}

interface WorkspaceState {
  workspaces: Workspace[]
  invites: WorkspaceInvite[]
  activeWorkspaceId: string | null
  isLoading: boolean
  error: string | null

  setWorkspaces: (workspaces: Workspace[]) => void
  setActiveWorkspaceId: (id: string | null) => void
  fetchWorkspaces: () => Promise<void>
  refreshActiveWorkspace: () => Promise<void>
  renameWorkspace: (id: string, name: string) => Promise<void>
  createWorkspace: (name: string) => Promise<string | null>
  fetchInvites: () => Promise<void>
  sendInvite: (workspaceId: string, email: string, role: string) => Promise<void>
  acceptInvite: (inviteId: string) => Promise<void>
  declineInvite: (inviteId: string) => Promise<void>
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      invites: [],
      activeWorkspaceId: null,
      isLoading: false,
      error: null,

      setWorkspaces: (workspaces) => set({ workspaces }),
      
      setActiveWorkspaceId: (id) => set({ activeWorkspaceId: id }),

      fetchWorkspaces: async () => {
        set({ isLoading: true, error: null })
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) throw new Error('Not authenticated')

          // Fetch workspaces where user is a member
          const { data, error } = await supabase
            .from('workspace_users')
            .select(`
              role,
              workspaces (
                id,
                name,
                rbac_settings
              )
            `)
            .eq('user_id', user.id)

          if (error) throw error

          const workspaces: Workspace[] = (data || [])
            .filter((item: any) => item.workspaces) // Ensure join succeeded
            .map((item: any) => ({
              id: item.workspaces.id,
              name: item.workspaces.name,
              role: item.role,
              rbac_settings: item.workspaces.rbac_settings
            }))

          set({ workspaces, isLoading: false })

          // Auto-select first workspace if none selected
          if (workspaces.length > 0 && !get().activeWorkspaceId) {
            const firstWorkspace = workspaces[0]
            if (firstWorkspace) {
              set({ activeWorkspaceId: firstWorkspace.id })
            }
          }
        } catch (err: any) {
          set({ error: err.message, isLoading: false })
        }
      },

      refreshActiveWorkspace: async () => {
        const { activeWorkspaceId } = get()
        if (!activeWorkspaceId) return

        try {
          const { data, error } = await supabase
            .from('workspaces')
            .select('id, name, rbac_settings')
            .eq('id', activeWorkspaceId)
            .single()

          if (error) throw error

          set((state) => ({
            workspaces: state.workspaces.map(w => 
              w.id === activeWorkspaceId ? { ...w, name: data.name, rbac_settings: data.rbac_settings } : w
            )
          }))
        } catch (err: any) {
          console.error('Failed to refresh active workspace:', err)
        }
      },

      createWorkspace: async (name: string) => {
        try {
          const { data, error } = await supabase.rpc('create_workspace', { p_name: name })
          if (error) throw error
          
          await get().fetchWorkspaces()
          set({ activeWorkspaceId: data })
          return data
        } catch (err: any) {
          console.error('Failed to create workspace:', err)
          return null
        }
      },

      renameWorkspace: async (id: string, name: string) => {
        try {
          const { error } = await supabase
            .from('workspaces')
            .update({ name })
            .eq('id', id)
          
          if (error) throw error
          
          await get().fetchWorkspaces()
        } catch (err: any) {
          console.error('Failed to rename workspace:', err)
          throw err
        }
      },

      fetchInvites: async () => {
        try {
          const { data, error } = await supabase
            .from('workspace_invites')
            .select(`
              *,
              workspaces (
                name
              )
            `)
            .eq('status', 'pending')

          if (error) throw error

          const invites = (data || []).map((item: any) => ({
            ...item,
            workspace_name: item.workspaces?.name
          }))

          set({ invites })
        } catch (err: any) {
          console.error('Failed to fetch invites:', err)
        }
      },

      sendInvite: async (workspaceId: string, email: string, role: string) => {
        const { error } = await supabase
          .rpc('request_workspace_invite', {
            p_workspace_id: workspaceId,
            p_email: email,
            p_role: role,
            p_request_id: globalThis.crypto?.randomUUID?.(),
          })
          .single()

        if (error) throw error
      },

      acceptInvite: async (inviteId: string) => {
        try {
          const { error } = await supabase.rpc('accept_invite', {
            p_invite_id: inviteId,
            p_request_id: globalThis.crypto?.randomUUID?.(),
          })
          if (error) throw error
          
          await get().fetchInvites()
          await get().fetchWorkspaces()
        } catch (err: any) {
          console.error('Failed to accept invite:', err)
          throw err
        }
      },

      declineInvite: async (inviteId: string) => {
        try {
          const { error } = await supabase.rpc('decline_workspace_invite', {
            p_invite_id: inviteId,
            p_request_id: globalThis.crypto?.randomUUID?.(),
          })

          if (error) throw error
          
          await get().fetchInvites()
        } catch (err: any) {
          console.error('Failed to decline invite:', err)
          throw err
        }
      }
    }),
    {
      name: 'hubspot-deploy-workspace',
      partialState: (state: any) => ({
        activeWorkspaceId: state.activeWorkspaceId,
      }),
    } as any
  )
)
